import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { DemoState, VirtualEmployee, Message, InterAgentMessage, CompanyData } from '../types';
import { initialCompanyData, createInitialAgents } from '../data/initialData';

interface DemoContextType {
  state: DemoState;
  dispatch: React.Dispatch<DemoAction>;
  startDemo: () => void;
  pauseDemo: () => void;
  resetDemo: () => void;
  setSpeed: (speed: 'slow' | 'normal' | 'fast') => void;
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void;
  addInterAgentMessage: (message: Omit<InterAgentMessage, 'id' | 'timestamp'>) => void;
  updateAgentStatus: (agentId: string, status: VirtualEmployee['status']) => void;
  updateAgentMind: (agentId: string, mindUpdate: Partial<VirtualEmployee['mind']>) => void;
  setTypingStatus: (sender: string, isTyping: boolean) => void;
}

type DemoAction = 
  | { type: 'START_DEMO' }
  | { type: 'PAUSE_DEMO' }
  | { type: 'RESET_DEMO' }
  | { type: 'SET_SPEED'; payload: 'slow' | 'normal' | 'fast' }
  | { type: 'ADD_MESSAGE'; payload: Message }
  | { type: 'ADD_INTER_AGENT_MESSAGE'; payload: InterAgentMessage }
  | { type: 'UPDATE_AGENT_STATUS'; payload: { agentId: string; status: VirtualEmployee['status'] } }
  | { type: 'UPDATE_AGENT_MIND'; payload: { agentId: string; mindUpdate: Partial<VirtualEmployee['mind']> } }
  | { type: 'SET_TYPING_STATUS'; payload: { sender: string; isTyping: boolean } }
  | { type: 'NEXT_STEP' }
  | { type: 'COMPLETE_DEMO' };

const initialState: DemoState = {
  currentStep: 0,
  isAutoPlaying: false,
  speed: 'normal',
  messages: [],
  interAgentMessages: [],
  agents: createInitialAgents(),
  companyData: initialCompanyData,
  isComplete: false,
  typingUsers: {}
};

function demoReducer(state: DemoState, action: DemoAction): DemoState {
  switch (action.type) {
    case 'START_DEMO':
      return { ...state, isAutoPlaying: true };
    
    case 'PAUSE_DEMO':
      return { ...state, isAutoPlaying: false };
    
    case 'RESET_DEMO':
      return {
        ...initialState,
        agents: createInitialAgents(),
        companyData: initialCompanyData
      };
    
    case 'SET_TYPING_STATUS':
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [action.payload.sender]: action.payload.isTyping
        }
      };
    
    case 'SET_SPEED':
      return { ...state, speed: action.payload };
    
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.payload]
      };
    
    case 'ADD_INTER_AGENT_MESSAGE':
      return {
        ...state,
        interAgentMessages: [...state.interAgentMessages, action.payload]
      };
    
    case 'UPDATE_AGENT_STATUS':
      return {
        ...state,
        agents: state.agents.map(agent =>
          agent.id === action.payload.agentId
            ? { ...agent, status: action.payload.status }
            : agent
        )
      };
    
    case 'UPDATE_AGENT_MIND':
      return {
        ...state,
        agents: state.agents.map(agent =>
          agent.id === action.payload.agentId
            ? { ...agent, mind: { ...agent.mind, ...action.payload.mindUpdate } }
            : agent
        )
      };
    
    case 'NEXT_STEP':
      return { ...state, currentStep: state.currentStep + 1 };
    
    case 'COMPLETE_DEMO':
      return { ...state, isComplete: true, isAutoPlaying: false };
    
    default:
      return state;
  }
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

export function DemoProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(demoReducer, initialState);

  const startDemo = () => dispatch({ type: 'START_DEMO' });
  const pauseDemo = () => dispatch({ type: 'PAUSE_DEMO' });
  const resetDemo = () => dispatch({ type: 'RESET_DEMO' });
  const setSpeed = (speed: 'slow' | 'normal' | 'fast') => dispatch({ type: 'SET_SPEED', payload: speed });

  const addMessage = (message: Omit<Message, 'id' | 'timestamp'>) => {
    const fullMessage: Message = {
      ...message,
      id: Date.now().toString(),
      timestamp: new Date().toISOString()
    };
    dispatch({ type: 'ADD_MESSAGE', payload: fullMessage });
  };

  const addInterAgentMessage = (message: Omit<InterAgentMessage, 'id' | 'timestamp'>) => {
    const fullMessage: InterAgentMessage = {
      ...message,
      id: Date.now().toString(),
      timestamp: new Date().toISOString()
    };
    dispatch({ type: 'ADD_INTER_AGENT_MESSAGE', payload: fullMessage });
  };

  const updateAgentStatus = (agentId: string, status: VirtualEmployee['status']) => {
    dispatch({ type: 'UPDATE_AGENT_STATUS', payload: { agentId, status } });
  };

  const updateAgentMind = (agentId: string, mindUpdate: Partial<VirtualEmployee['mind']>) => {
    dispatch({ type: 'UPDATE_AGENT_MIND', payload: { agentId, mindUpdate } });
  };

  const setTypingStatus = (sender: string, isTyping: boolean) => {
    dispatch({ type: 'SET_TYPING_STATUS', payload: { sender, isTyping } });
  };

  return (
    <DemoContext.Provider value={{
      state,
      dispatch,
      startDemo,
      pauseDemo,
      resetDemo,
      setSpeed,
      addMessage,
      addInterAgentMessage,
      updateAgentStatus,
      updateAgentMind,
      setTypingStatus
    }}>
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const context = useContext(DemoContext);
  if (context === undefined) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
}